import { Flex, Text } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import MentionCardCreatorRow from './MentionCardCreatorRow';
import { MentionCardStats } from './MentionCardStats';
import { mentionCardFooterPadding } from './mentionCardStyle';

interface MentionCardFooterProps {
  mention: Mention;
  /** Show the creator identity (avatar + handle). When false, the creator is
   *  already the surrounding context, so only the date sits beside the stats. */
  showCreator: boolean;
  /** Denser sizing for narrow contexts (the contact drawer). */
  compact: boolean;
}

/**
 * Single-row card footer: creator identity (or just the date) on the left,
 * platform-aware stats on the right.
 */
const MentionCardFooter: React.FC<MentionCardFooterProps> = ({ mention, showCreator, compact }) => {
  const { t } = useTranslation();
  const timeAgo = DateTime.fromISO(mention.publishedAt || mention.createdAt).toRelative({
    style: 'short',
  });
  const affiliateMembership = mention.author.affiliateMembership;
  const isAffiliate =
    Boolean(affiliateMembership?.isActive) || (!affiliateMembership && mention.author.isEnrolled);
  const affiliateLabel = t('mentions.affiliateBadge.label', { defaultValue: 'Affiliate' });

  return (
    <Flex
      alignItems="center"
      flexWrap={showCreator ? 'wrap' : 'nowrap'}
      rowGap={1.5}
      gap={2}
      {...mentionCardFooterPadding(compact)}
    >
      {showCreator ? (
        <MentionCardCreatorRow
          username={mention.author.username}
          profilePictureUrl={mention.author.profilePictureUrl}
          timeAgo={timeAgo}
          isAffiliate={isAffiliate}
          affiliateLabel={affiliateLabel}
        />
      ) : (
        <Text fontSize={compact ? '10.5px' : '11px'} color="primary.400" flexShrink={0}>
          {timeAgo}
        </Text>
      )}

      <MentionCardStats mentionId={mention.id} platform={mention.platform} compact={compact} />
    </Flex>
  );
};

export default MentionCardFooter;
